import React, { useCallback, useRef } from 'react';

import { Col, Row, Table } from 'antd';
import { useFilters, useWindowSize } from 'hooks';
import { Input } from 'components';
import { useGetRelationGroupOverviewQuery } from 'services';
import { TABLE_STICKY } from 'constant/table';

import AddNewRelation from '../add-new-relation-group';
import EmptyState from '../empty-state';
import FilteredEmptyState from '../filtered-empty-state';
import useCreateRelationGroup from '../use-create-relation-group';

import useColumn from './use-column';

const { Search } = Input;

const DEFAULT_FILTERS = { page: 1, itemsPerPage: 20 };
const INTERNAL_KEYS = ['page', 'itemsPerPage', '_sort', '_sortDir'];

function ArticleRelationDataTable() {
  const { height } = useWindowSize();
  const debounce = useRef();
  const { filters, setFilters, apiParams } = useFilters(DEFAULT_FILTERS);
  const column = useColumn({ filters });
  const { data, isFetching } = useGetRelationGroupOverviewQuery(apiParams);
  const { onSelect, isLoading } = useCreateRelationGroup();

  const hasActiveFilters = Object.keys(filters).some((k) => !INTERNAL_KEYS.includes(k));

  const resetFilters = useCallback(() => {
    setFilters({ ...DEFAULT_FILTERS });
  }, [setFilters]);

  const pagination = {
    defaultCurrent: 1,
    defaultPageSize: 20,
    current: filters.page,
    pageSize: filters.itemsPerPage,
    total: data?.['hydra:totalItems'] ?? 0,
    showTotal: (total) => `Toplam Madde: ${total}`,
    position: ['bottomRight'],
    responsive: true,
    showSizeChanger: true,
    pageSizeOptions: [10, 20, 50, 100],
  };

  /** Filtrelenebilir sütun key'leri — onChange'de bunlar temizlenip yeniden set edilir */
  const filterKeys = ['type[]', 'hasChecked', 'hasNoRelation', 'isAbandoned', 'isDraft', 'isCancelled'];

  const handleTableChange = (tablePagination, tableFilters, sorter) => {
    const { current, pageSize } = tablePagination;

    // Mevcut sort ve filter key'lerini temizle
    const { _sort: _s, _sortDir: _d, ...rest } = filters;
    const cleaned = Object.fromEntries(
      Object.entries(rest).filter(([key]) => !filterKeys.includes(key)),
    );

    // Sort
    const sortParams = sorter.order
      ? { _sort: sorter.field, _sortDir: sorter.order === 'ascend' ? 'asc' : 'desc' }
      : {};

    // Filter — antd'den gelen tableFilters'ı API formatına dönüştür
    const filterParams = {};
    if (tableFilters.type?.length) {
      filterParams['type[]'] = tableFilters.type;
    }
    if (tableFilters.hasChecked?.length) {
      filterParams.hasChecked = tableFilters.hasChecked[0];
    }
    if (tableFilters.relationsCount?.length) {
      filterParams.hasNoRelation = tableFilters.relationsCount[0];
    }
    if (tableFilters.isAbandoned?.length) {
      filterParams.isAbandoned = tableFilters.isAbandoned[0];
    }
    if (tableFilters.isDraft?.length) {
      filterParams.isDraft = tableFilters.isDraft[0];
    }
    if (tableFilters.isCancelled?.length) {
      filterParams.isCancelled = tableFilters.isCancelled[0];
    }

    setFilters({
      ...cleaned,
      ...sortParams,
      ...filterParams,
      page: pageSize === filters.itemsPerPage ? current : 1,
      itemsPerPage: pageSize,
    });
  };

  const searchName = (text) => {
    clearTimeout(debounce.current);
    debounce.current = setTimeout(() => {
      setFilters({ ...filters, title: text, page: 1 });
    }, 300);
  };

  return (
    <>
      <Row
        justify="space-between"
        align="middle"
        className="gx-mb-3"
      >
        <Col
          xs={14}
          md={11}
        >
          <Search
            name="relationSearch"
            onSearch={searchName}
            defaultValue={filters?.title}
            placeholder="Madde adına göre arayın..."
            allowClear
            enterButton
          />
        </Col>
        <Col className="gx-pr-3">
          <AddNewRelation
            onSelect={onSelect}
            isSelecting={isLoading}
          />
        </Col>
      </Row>
      {!isFetching && !hasActiveFilters && data?.['hydra:totalItems'] === 0 ? (
        <EmptyState />
      ) : (
        <Table
          rowKey="id"
          columns={column}
          dataSource={data?.['hydra:member']}
          loading={isFetching}
          pagination={pagination}
          onChange={handleTableChange}
          scroll={{ x: 2500, y: height - 400 }}
          sticky={TABLE_STICKY}
          locale={{ emptyText: <FilteredEmptyState onReset={resetFilters} /> }}
        />
      )}
    </>
  );
}

export default ArticleRelationDataTable;
